<script setup>
    
    const { $event } = useNuxtApp();

    // Header search modal (Asana 1217333292818333).
    const searchOpen = ref(false);

    const props = defineProps({
        hasUnreadNotifications: {
            type: Boolean,
        },
        isStatic: {
            type: Boolean,
        },
    });

    const { signOut, isLoggedIn, user } = useAuth()
    
    const open = ref(false);
    const activeIndex = ref(null);
    const degreeRotation = ref(0);
    const route = useRoute();

    // Background scroll is locked while the drawer is open — iOS-safe, unlike
    // the body.style.overflow toggling this used to do.
    useScrollLock(open);

    watch(route, () => {
        open.value = false;
    });

    const toggleMenu = () => {
        open.value = !open.value;
        if (open.value) $event('globalHeaderNavOpen');
    };

    // Escape closes the drawer and puts focus back on the hamburger, the
    // control that opened it.
    const menuTrigger = ref(null);
    const onKeydown = (event) => {
        if (event.key !== 'Escape' || !open.value || event.defaultPrevented) return;
        event.preventDefault();
        open.value = false;
        activeIndex.value = null;
        degreeRotation.value = 0;
        menuTrigger.value?.focus();
    };
    onMounted(() => document.addEventListener('keydown', onKeydown));
    onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

    const toggleActive = (index) => {
        degreeRotation.value = 0;
        {
            if (activeIndex.value === index) {
                activeIndex.value = null;
                degreeRotation.value -= 90;
            } else {
                activeIndex.value = index;
                degreeRotation.value += 90;
            }
        }
    };

    const menuOff = () => {
        open.value = !open.value;
        activeIndex.value = null;
        degreeRotation.value = 0;
    };

    const skipToContent = () => {
        const anchor = document.getElementById('main');
        if (anchor) {
            anchor.focus();
        }
    };

    const displayNotifications = () => {
        $event('showNotifications');
    };
</script>


<template>
    <header class="global-header" :class="{ open: open }">
        <div class="meta-actions">
            <a @click.prevent="skipToContent" href="#main" class="skip-link sr-only sr-only-focusable">Skip to Content</a>
        </div>

        <div class="container primary-navigation" id="primary-navigation">
            <UtilitiesFocusTrap :enabled="open" class="trap-wrapper">
                <div class="mobile-trigger-wrapper">
                    <button
                        ref="menuTrigger"
                        class="mobile-trigger"
                        type="button"
                        aria-label="Menu"
                        aria-controls="mobile-menu"
                        :aria-expanded="open"
                        :class="{ showing: open }"
                        @click="toggleMenu">
                        <SvgsNavHamburger />
                    </button>
                </div>
                <transition name="slide">
                    <!-- Rules between items are drawn by `.ruled` (a pseudo-element),
                         not <hr>: an <hr> is not allowed as a child of <ul>, and
                         neither is a bare <ul> — each sub-menu now sits inside the
                         <li> whose button toggles it. -->
                    <nav id="mobile-menu" aria-label="Menu" class="menubar" :class="{ showing: open }">
                        <div class="container white-bg">
                            <ul class="nav-items">
                                <li class="ruled ruled-top">
                                    <NuxtLink to="/" @click.native="menuOff">Open House Chicago 2026</NuxtLink>
                                </li>
                                <li class="indented ruled ruled-top">
                                    <NuxtLink to="/neighborhoods" @click.native="menuOff">Neighborhood Areas</NuxtLink>
                                </li>
                                <li class="indented ruled ruled-top">
                                    <NuxtLink to="/sites" @click.native="menuOff">Sites</NuxtLink>
                                </li>
                                <li class="indented ruled ruled-top">
                                    <NuxtLink to="/itineraries" @click.native="menuOff">Itineraries</NuxtLink>
                                </li>
                                <li class="indented ruled ruled-top">
                                    <NuxtLink to="/events" @click.native="menuOff">Events</NuxtLink>
                                </li>
                                <li class="indented ruled ruled-top">
                                    <a href="https://www.architecture.org/open-house-chicago/photo-competition-2026" @click.native="menuOff">Photo Competition</a>
                                </li>
                                <li class="indented ruled ruled-top">
                                    <!-- Not /about-ohc: that page is a full-window iframe of
                                         this same URL. Opens in a new tab so the app, and the
                                         iOS/Android wrapper around it, is not navigated away from. -->
                                    <a href="https://www.architecture.org/open-house-chicago/about" target="_blank" rel="noopener noreferrer" @click="menuOff">About Open House Chicago (OHC)</a>
                                </li>
                                <li class="indented">
                                    <NuxtLink to="/archive" @click.native="menuOff">Archives</NuxtLink>
                                </li>
                                <li class="my-ohc-menu">
                                    <GlobalCtaLargeButton @click.native="menuOff" color="white" link="/my-ohc" class="connect-button">My OHC</GlobalCtaLargeButton>
                                    <NuxtLink to="/login" class="login" v-if="!isLoggedIn">
                                        <SvgsMy class="my-ohc-icon" /> Log in to My OHC
                                    </NuxtLink>
                                    <div v-else class="logout">
                                        <p>Logged in as <strong>{{ user.email }}</strong></p>
                                        <button type="button" @click.prevent="signOut">Log out</button>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <div class="container bottom-nav">
                            <ul class="nav-items">
                                <li class="ruled">
                                    <button type="button" class="sub-nav" aria-controls="mobile-menu-about" :aria-expanded="activeIndex === 'about'" @click="toggleActive('about')">
                                        <span>About CAC</span>
                                        <span :style="[activeIndex === 'about' ? { transform: `rotate(${degreeRotation}deg)` } : {}]" class="transition">
                                            <SvgsNavCaret />
                                        </span>
                                    </button>
                                    <ul id="mobile-menu-about" class="sub-nav-content" :class="activeIndex === 'about' ? 'is-visible' : ''">
                                        <li>
                                            <NuxtLink to="/about-cac" @click.native="menuOff">All About CAC</NuxtLink>
                                        </li>
                                        <li>
                                            <a href="/about/land-acknowledgment/" @click.native="menuOff">Land Acknowledgment</a>
                                        </li>
                                        <li>
                                            <NuxtLink to="/about/contact-us" @click.native="menuOff">Contact Us</NuxtLink>
                                        </li>
                                    </ul>
                                </li>
                                <li class="ruled">
                                    <a href="/memberships" @click.native="menuOff">Membership</a>
                                </li>
                                <li class="ruled">
                                    <a href="https://cart.architecture.org/donate/q/support" @click.native="menuOff">Donate</a>
                                </li>
                                <li class="ruled">
                                    <button type="button" class="sub-nav" aria-controls="mobile-menu-tours" :aria-expanded="activeIndex === 'tours'" @click="toggleActive('tours')">
                                        <span>City Tours</span>
                                        <span :style="[activeIndex === 'tours' ? { transform: `rotate(${degreeRotation}deg)` } : {}]" class="transition">
                                            <SvgsNavCaret />
                                        </span>
                                    </button>
                                    <ul id="mobile-menu-tours" class="sub-nav-content" :class="activeIndex === 'tours' ? 'is-visible' : ''">
                                        <li>
                                            <NuxtLink to="/city-tours" @click.native="menuOff">City Tours</NuxtLink>
                                        </li>
                                        <li>
                                            <a href="/city-tours/river-cruise" @click.native="menuOff">River Cruise</a>
                                        </li>
                                        <li>
                                            <a href="/city-tours/bus" @click.native="menuOff">Bus Tours</a>
                                        </li>
                                        <li>
                                            <a href="/city-tours/walking" @click.native="menuOff">Walking Tours</a>
                                        </li>
                                        <li>
                                            <NuxtLink to="/city-tours/accessibility" @click.native="menuOff">Accessibility &amp; Safety</NuxtLink>
                                        </li>
                                    </ul>
                                </li>
                                <li class="ruled">
                                    <button type="button" class="sub-nav" aria-controls="mobile-menu-visit" :aria-expanded="activeIndex === 'visit'" @click="toggleActive('visit')">
                                        <span>Visit the Center</span>
                                        <span :style="[activeIndex === 'visit' ? { transform: `rotate(${degreeRotation}deg)` } : {}]" class="transition">
                                            <SvgsNavCaret />
                                        </span>
                                    </button>
                                    <ul id="mobile-menu-visit" class="sub-nav-content" :class="activeIndex === 'visit' ? 'is-visible' : ''">
                                        <li>
                                            <NuxtLink to="/visit" @click.native="menuOff">Center Information</NuxtLink>
                                        </li>
                                        <li>
                                            <a href="/visit/buy-center-tickets" @click.native="menuOff">Buy Tickets</a>
                                        </li>
                                        <li>
                                            <NuxtLink to="/city-tours/accessibility" @click.native="menuOff">Accessibility</NuxtLink>
                                        </li>
                                    </ul>
                                </li>
                                <li class="ruled">
                                    <NuxtLink to="/exhibitions" @click.native="menuOff">Current Exhibitions</NuxtLink>
                                </li>
                                <li class="ruled">
                                    <button type="button" class="sub-nav" aria-controls="mobile-menu-events" :aria-expanded="activeIndex === 'events'" @click="toggleActive('events')">
                                        <span>Events &amp; Programs</span>
                                        <span :style="[activeIndex === 'events' ? { transform: `rotate(${degreeRotation}deg)` } : {}]" class="transition">
                                            <SvgsNavCaret />
                                        </span>
                                    </button>
                                    <ul id="mobile-menu-events" class="sub-nav-content" :class="activeIndex === 'events' ? 'is-visible' : ''">
                                        <li>
                                            <a href="/programs-events" @click.native="menuOff">All Events &amp; Programs</a>
                                        </li>
                                        <li>
                                            <NuxtLink to="/programs-events/youth" @click.native="menuOff">Teen &amp; Youth Programs</NuxtLink>
                                        </li>
                                    </ul>
                                </li>
                                <li class="ruled">
                                    <NuxtLink to="/archive" @click.native="menuOff">Open House Chicago Archive</NuxtLink>
                                </li>
                                <li>
                                    <NuxtLink to="/city-tours/accessibility" @click.native="menuOff">Accessibility</NuxtLink>
                                </li>
                            </ul>
                        </div>
                    </nav>
                </transition>
            </UtilitiesFocusTrap>
            <NuxtLink :to="'/'" class="logo ohc-cac-logo" aria-label="Open House Chicago home">
                <SvgsOhcCacStacked />
            </NuxtLink>
            <!-- Order per Figma 4933-40092: search, login, notifications — with the
                 hamburger after them (it sits outside this group, in the focus trap,
                 and is placed last with `order`). -->
            <div class="header-actions">
                <!-- Was a NuxtLink to /sites/, which just navigated to the sites
                     page's inline search box. Now opens the Algolia search modal
                     (Asana 1217333292818333). -->
                <button
                    type="button"
                    class="header-icon header-search"
                    aria-label="Search"
                    :aria-expanded="searchOpen"
                    @click="searchOpen = true">
                    <SvgsNavSearch />
                </button>
                <NuxtLink
                    :to="isLoggedIn ? '/my-ohc/' : '/login'"
                    class="header-icon header-account"
                    :aria-label="isLoggedIn ? 'My OHC' : 'Log in'"
                >
                    <SvgsNavAccount />
                </NuxtLink>
                <button type="button" class="header-icon notifications" :class="{ 'has-new': hasUnreadNotifications }" aria-label="Notifications" @click="displayNotifications">
                    <SvgsNavBell />
                </button>
            </div>
        </div>
    </header>

    <ClientOnly>
        <GlobalSearchModal v-if="searchOpen" @close="searchOpen = false" />
    </ClientOnly>
</template>

<style lang="scss" scoped>
    header {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        z-index: 99;
        background: $gray-light;

        > .container {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: var(--header-height, #{$header_height});
            padding-top: 10px;
            padding-bottom: 10px;
            padding-left: 16px;
            padding-right: 16px;
        }

        // Both breakpoints put the logo hard left and the icons right, per the
        // comp's mobile and desktop frames. The hamburger lives inside the focus
        // trap (which also owns the slide-out), so rather than move it in the DOM
        // and risk the trap, it's ordered last visually.
        > .container {
            .logo {
                order: 1;
                margin-right: auto;
            }

            .header-actions {
                order: 2;
            }

            .trap-wrapper {
                order: 3;
            }
        }

        // Desktop navbar per Figma 4933-40092: white and taller, with the teal
        // nav row below it replacing the hamburger drawer entirely.
        @media (min-width: $desktop) {
            background: $white;

            > .container {
                max-width: $hd;
                margin: 0 auto;
            }

            // No hamburger on desktop — nav-desktop.vue is the navigation there.
            // Hiding the whole trap wrapper takes the drawer with it, so it can't
            // be opened at this width.
            .trap-wrapper {
                display: none;
            }
        }

        // Drop the gutter once the container is at full width, so the logo's left
        // edge lines up with the nav row beneath it (both at x=145 in the comp's
        // 1440 frame). nav-desktop-inner does the same.
        @media (min-width: $hd + 32px) {
            > .container {
                padding-left: 0;
                padding-right: 0;
            }
        }
    }

    .header-actions {
        display: flex;
        align-items: center;

        // Figma's mobile header frame spaces these at exactly 16px.
        gap: 16px;
        margin-right: 16px; // same 16px before the hamburger
    }

    // Icon sizes come straight from the comp's two header frames. They differ
    // per icon (the bell is taller than it is wide, the hamburger is square), so
    // each is sized by width with height following its own aspect ratio.
    //
    // The icons are only 16-22px, which was also their whole tap target. The
    // padding grows each hit area to at least 44x44 (Apple's minimum); the
    // equal negative margin takes the padding back out of the layout, so the
    // icons sit exactly where the comp puts them.
    $icon-hit-pad: 14px;

    .header-icon {
        display: block;
        color: $black;
        padding: $icon-hit-pad;
        margin: -$icon-hit-pad;

        svg {
            display: block;
            height: auto;
        }
    }

    // Now a <button>, not a link — reset the UA chrome so it sits identically to
    // the account and notification icons beside it.
    .header-search {
        border: 0;
        background: none;
        cursor: pointer;
        color: inherit;
    }

    .header-search svg { width: 19px; }
    .header-account svg { width: 18px; }
    .notifications svg { width: 16.27px; }

    // Larger icons belong to the desktop navbar, which only exists at $desktop.
    @media (min-width: $desktop) {
        .header-search svg { width: 22px; }
        .header-account svg { width: 22px; }
        .notifications svg { width: 20.55px; }
    }

    .white-bg {
        background-color: white;
        padding-bottom: 15px;
        padding-left: 16px;
        padding-right: 16px;
    }

    // Was the <li> itself, clickable but not focusable or announced as a
    // control. Now a <button> filling the same row; destyle has already
    // stripped its chrome and it inherits the item's type.
    .sub-nav {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        text-align: left;
        cursor: pointer;
    }

    .logout {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 16px;
        font-size: 13px;
        font-weight: 450;
        text-transform: uppercase;

        button {
            font-size: 13px;
            font-weight: 450;
            color: $red;
            text-decoration: underline;
        }
    }

    .bottom-nav {
        padding: 0 16px;
    }

    .sub-nav-content {
        margin-left: 20px;
        visibility: hidden;
        height: 0;
        opacity: 0;
        transition:
            height 0.5s,
            opacity 0.2s linear;

        > li {
            font-family: $book;
            font-weight: 500;
            font-size: 16px;
            line-height: 24px;
            padding-top: 12px;
        }

        &.is-visible {
            visibility: visible;
            height: 100%;
            opacity: 1;
        }
    }

    .transition {
        transition: transform 0.3s ease-out;
        line-height: 12px;
    }

    .menubar {
        // `fixed`, not `absolute`: as an absolute box its containing block is the
        // header's .container, which is capped at $hd and centred on desktop —
        // that penned the drawer into a 1150px column instead of the viewport.
        // Fixed also means top/height are measured against the viewport, so the
        // drawer keeps filling the screen below the header at any breakpoint.
        position: fixed;
        height: calc(100svh - var(--header-height, #{$header_height}));
        width: 0;
        top: var(--header-height, #{$header_height});
        left: 0;
        right: 0;

        // No desktop override needed: the drawer only exists below $desktop,
        // where the header is the only fixed chrome above it.
        overflow-y: auto;
        overflow-x: hidden;
        z-index: 5;
        background-color: $white;
        transition: transform 0.5s ease;
        transform: translate3d(-100vw, 0, 0);
        padding-bottom: 30px;
        -webkit-overflow-scrolling: touch;
        background: $gray-light;
        z-index: 9999;

        nav {
            display: none;
        }

        &:after {
            content: '';
            position: fixed;
            top: 0;
            left: 100%;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            background: linear-gradient(90deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 70%, rgba(0, 0, 0, 0) 100%);
            transition: 0.5s ease;
            width: 0;
            z-index: 9999;
        }

        &.showing {
            width: min(100%, 500px);
            z-index: 9999;

            transform: translate3d(0, 0, 0);
            .nav-items {
                display: flex;
            }

            &:after {
                z-index: 9999;
                width: calc(500px + 100vw);
            }
        }

        // The rule under an item — these were <hr>s between the <li>s, which a
        // <ul> may not contain. Same metrics: 10px below the item, a 2px line
        // (1px border all round on a zero-height box).
        .ruled::after {
            content: '';
            display: block;
            margin-top: 10px;
            border: 1px solid $gray-light-2;
        }

        .ruled-top::after {
            border-color: $turquoise-light;
        }
    }

    .nav-items {
        display: none;
        flex-direction: column;
        -webkit-overflow-scrolling: touch;

        > li {
            margin-top: 15px;
            font-family: $body;
            font-size: 18px;
            font-weight: 800;
            line-height: 24px;
        }
    }

    .mobile-trigger-wrapper {
        display: block;

        .mobile-trigger {
            cursor: pointer;
            display: block;
            // 18px is the comp's hamburger size (it was 20px, which stretched the
            // 18x18 icon). No desktop size needed — the hamburger only exists
            // below $desktop, alongside the smaller icon set it was drawn with.
            //
            // The padding makes it a 44x44 tap target around the 18px icon and
            // the negative margin keeps that out of the layout — see
            // .header-icon. content-box so the 18px stays the icon's width.
            width: 18px;
            box-sizing: content-box;
            padding: 13px;
            margin: -13px;
            color: $black;
            border: none;
            position: relative;
            z-index: 1;
            overflow: hidden;

            svg {
                display: block;
                width: 100%;
                height: auto;
            }

            // :deep() is required now that the icon is a child component — these
            // paths no longer carry this file's scope attribute, so an unscoped
            // `.bar` selector silently stopped matching and the burger never
            // animated into an X.
            :deep(.bar) {
                transition: all 0.25s linear;
                transform: translate(0, 0) rotate(0deg);
                will-change: transform, opacity;
            }

            // Recomputed for the Figma icon's 18x18 viewBox (bars at y=2/9/16);
            // the old offsets were measured against the previous 22x19 art and
            // no longer meet in the middle. Each bar rotates about its own centre
            // and slides to the middle bar's line.
            &.showing {
                :deep(.bar-1) {
                    transform-origin: 9px 2px;
                    transform: translateY(7px) rotate(45deg);
                }
                :deep(.bar-2) {
                    opacity: 0;
                }
                :deep(.bar-3) {
                    transform-origin: 9px 16px;
                    transform: translateY(-7px) rotate(-45deg);
                }
            }
        }
    }

    // Stacked OHC/CAC lockup (2026 menu refresh). The SVG is 612x184.5, so a
    // fixed width keeps it inside the 59px header once the 10px vertical
    // padding is taken off; height follows the aspect ratio.
    .ohc-cac-logo {
        display: flex;
        align-items: center;

        // Figma sizes the lockup at 155px in the 60px mobile header and 210px in
        // the 88px desktop navbar.
        svg {
            display: block;
            width: 155px;
            height: auto;
        }

        @media (min-width: $desktop) {
            svg {
                width: 210px;
            }
        }
    }

    .my-ohc-menu {
        margin-bottom: 16px;
        
        .cta-large.container.connect-button { margin-bottom: 16px; display: block; }
        
        .login {
            display: flex;
            align-items: center;
            gap: 13px;
            font-size: 18px;
            font-style: normal;
            font-weight: 500;
            line-height: 1em;

            &:hover {
                color: $red;
                
                .tag {
                    background: $red;
                }
            }

            .my-ohc-icon {
                width: 14px;
                height: 17px;
            }

            .tag {
                color: $white;
                margin-bottom: 2px;
                background-color: $turquoise;
                padding: 10px 10px 8px 10px;
                font-size: 9px;
                font-style: normal;
                font-weight: 800;
                line-height: 10px;
                letter-spacing: 0.72px;
                text-transform: uppercase;                
            }
        }
    }

    .notifications {
        display: inline-block;
        position: relative;
        // The comp draws this as a 7px circle (r=3.5) sitting 7.45px left of the
        // bell — it was 8px at a 6px gap. The comp also treats dot+bell as one
        // "Announcements" box, so the 16px gap is measured to the DOT: the dot is
        // padded into this button rather than floated outside it, which would
        // otherwise eat almost all of that 16px.
        // Both offsets sit on top of the tap-target padding (.header-icon).
        &.has-new {
            padding-left: calc(#{$icon-hit-pad} + 14.45px);

            &::after {
                content: '';
                position: absolute;
                top: calc(50% - 3.5px);
                left: $icon-hit-pad;
                width: 7px;
                height: 7px;
                background: $red;
                border-radius: 50%;
            }
        }
    }

    .connect-button.cta-large {
        padding: 0;
        
        .button { width: 100%; }
    }
</style>
